import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  datesForViewTimeframe,
  getMinervaMetricCubes,
  isMinervaDataRequest,
  toMinervaActivityQueries,
} from '../utils/minervaDataRequest'
import type { MinervaCubeBridge } from '../utils/useMinervaCubeBridge'
import { useMinervaAskMutation } from './minervaAsk.hook'
import {
  addMinervaQuestion,
  buildMinervaAskInput,
  closeMinervaChat,
  failMinervaQuestion,
  isMinervaChatGone,
  isMinervaTurnLimit,
  keepMinervaChatTimeframe,
  type MinervaChatContent,
  type MinervaChatEntry,
  type MinervaChatSwitchContent,
  type MinervaChats,
  openMinervaChat,
  reachMinervaTurnLimit,
  receiveMinervaAnswer,
  receiveMinervaDataRequest,
  resetMinervaChatId,
  retryMinervaQuestion,
  stopMinervaQuestion,
  switchMinervaChatContext,
} from './minervaChat'
import { parseMinervaNavigation } from './minervaNavigation'

type MinervaChatContextValue = {
  chats: MinervaChats
  openChat: (key: string, content: MinervaChatContent) => void
  closeChat: (key: string) => void
  ask: (key: string, question: string, bridge: MinervaCubeBridge | null) => void
  stop: (key: string) => void
  switchContext: (key: string, content: MinervaChatSwitchContent) => void
  keepTimeframe: (key: string, timeframe: string) => void
  retry: (
    key: string,
    messageId: string,
    bridge: MinervaCubeBridge | null,
  ) => void
}

const MinervaChatContext = createContext<MinervaChatContextValue | null>(null)

/**
 * What `MinervaChatLayout` gives the chat: the element the open panel is
 * portalled into (null until mounted) and the dashboard's Cube bridge (null
 * outside a `CubeProvider`). The context itself is null outside a layout.
 */
export const MinervaChatLayoutContext = createContext<{
  panelSlot: HTMLElement | null
  cubeBridge: MinervaCubeBridge | null
} | null>(null)

const isPending = (chats: MinervaChats, key: string, pendingRequest: string) =>
  chats[key]?.pendingRequest === pendingRequest

type MinervaRequest = {
  key: string
  question: string
  messageId: string
  pendingRequest: string
  bridge: MinervaCubeBridge | null
}

/**
 * Holds every view's chat in memory for the whole session. It sits in the
 * authenticated app shell, so chats survive in-app navigation and are lost on
 * reload, in a new tab and on logout, as the UX spec asks.
 */
export const MinervaChatProvider = ({ children }: PropsWithChildren) => {
  const [askMinerva] = useMinervaAskMutation()
  const [chats, setChats] = useState<MinervaChats>({})
  // Mirrors `chats` synchronously, so a request that settles later reads the
  // chat as it is then, not as it was when the request started.
  const chatsRef = useRef(chats)
  const requestCount = useRef(0)

  const update = useCallback(
    (change: (chats: MinervaChats) => MinervaChats) => {
      chatsRef.current = change(chatsRef.current)
      setChats(chatsRef.current)
    },
    [],
  )

  /**
   * One question: ask, and if minerva asks for data, run its queries and ask
   * once more with the rows. The backend allows one data request per
   * question, so there is no third leg.
   */
  const request = useCallback(
    async ({
      key,
      question,
      messageId,
      pendingRequest,
      bridge,
    }: MinervaRequest): Promise<void> => {
      const chat = chatsRef.current[key]
      if (!chat) return
      try {
        // A new chat, including a restart after expiry, gets the catalog.
        const catalog =
          chat.chatId === null && bridge
            ? await bridge.getCatalog(getMinervaMetricCubes(chat.metrics))
            : null
        const { data } = await askMinerva({
          variables: {
            input: buildMinervaAskInput({
              question,
              messageId,
              chatId: chat.chatId,
              viewContext: chat.viewContext,
              metrics: chat.metrics,
              cardSummary: chat.sendsCardSummary ? chat.summary : undefined,
              catalog,
            }),
          },
        })
        if (!data) throw new Error('minervaAsk returned no data')
        const asked = data.minervaAsk
        let response = asked

        if (isMinervaDataRequest(asked.result)) {
          // Without a bridge no catalog went out, so this shouldn't happen.
          if (!bridge)
            throw new Error('minervaAsk asked for data it cannot get')
          if (!isPending(chatsRef.current, key, pendingRequest)) return
          const { reason } = asked.result
          const queries: unknown = JSON.parse(asked.result.queries)
          update(current =>
            receiveMinervaDataRequest(current, key, {
              pendingRequest,
              activity: {
                reason: reason ?? null,
                queries: toMinervaActivityQueries(queries),
              },
            }),
          )
          const dataResult = await bridge.runDataQueries(queries, {
            globalFilters: chat.globalFilters,
            defaultDateRange: datesForViewTimeframe(
              chat.viewContext?.timeframe,
            ),
          })
          if (!isPending(chatsRef.current, key, pendingRequest)) return
          // The request leg opened the chat but stored no turn, so its
          // chatId is the one to use, and the messageId is still unused.
          const { data: answerData } = await askMinerva({
            variables: {
              input: {
                question,
                messageId,
                chatId: asked.chatId,
                dataResult,
              },
            },
          })
          if (!answerData) throw new Error('minervaAsk returned no data')
          response = answerData.minervaAsk
        }

        const { chatId, result } = response
        if (result.__typename !== 'MinervaAnswer') {
          throw new Error('minervaAsk returned no answer')
        }
        update(current =>
          receiveMinervaAnswer(current, key, {
            pendingRequest,
            chatId,
            messageId,
            answer: result.text,
            navigation: parseMinervaNavigation(result.navigationItem),
            timestamp: new Date(),
          }),
        )
      } catch (error) {
        if (!isPending(chatsRef.current, key, pendingRequest)) return
        // The chat expired or is unknown. The provider still holds the
        // context, so the question starts a new one.
        if (chatsRef.current[key]?.chatId && isMinervaChatGone(error)) {
          update(current => resetMinervaChatId(current, key))
          return request({ key, question, messageId, pendingRequest, bridge })
        }
        if (isMinervaTurnLimit(error)) {
          update(current => reachMinervaTurnLimit(current, key, pendingRequest))
          return
        }
        update(current => failMinervaQuestion(current, key, pendingRequest))
      }
    },
    [askMinerva, update],
  )

  const openChat = useCallback(
    (key: string, content: MinervaChatContent) =>
      update(current => openMinervaChat(current, key, content)),
    [update],
  )
  const closeChat = useCallback(
    (key: string) =>
      update(current =>
        closeMinervaChat(stopMinervaQuestion(current, key), key),
      ),
    [update],
  )
  const stop = useCallback(
    (key: string) => update(current => stopMinervaQuestion(current, key)),
    [update],
  )
  const switchContext = useCallback(
    (key: string, content: MinervaChatSwitchContent) =>
      update(current =>
        switchMinervaChatContext(
          stopMinervaQuestion(current, key),
          key,
          content,
          { messageId: crypto.randomUUID(), timestamp: new Date() },
        ),
      ),
    [update],
  )
  const keepTimeframe = useCallback(
    (key: string, timeframe: string) =>
      update(current =>
        keepMinervaChatTimeframe(current, key, timeframe, {
          messageId: crypto.randomUUID(),
          timestamp: new Date(),
        }),
      ),
    [update],
  )
  const ask = useCallback(
    (key: string, question: string, bridge: MinervaCubeBridge | null) => {
      const chat = chatsRef.current[key]
      if (chat?.pendingRequest || chat?.turnLimitReached) return
      // `messageId` is made once per typed question and reused on Retry.
      const messageId = crypto.randomUUID()
      const pendingRequest = String(++requestCount.current)
      update(current =>
        addMinervaQuestion(current, key, {
          messageId,
          question,
          timestamp: new Date(),
          pendingRequest,
        }),
      )
      void request({ key, question, messageId, pendingRequest, bridge })
    },
    [request, update],
  )
  const retry = useCallback(
    (key: string, messageId: string, bridge: MinervaCubeBridge | null) => {
      const chat = chatsRef.current[key]
      const message = chat?.messages.find(
        candidate =>
          candidate.role === 'user' && candidate.messageId === messageId,
      )
      if (!message || chat?.pendingRequest || chat?.turnLimitReached) return
      const pendingRequest = String(++requestCount.current)
      update(current =>
        retryMinervaQuestion(current, key, { messageId, pendingRequest }),
      )
      void request({
        key,
        question: message.content,
        messageId,
        pendingRequest,
        bridge,
      })
    },
    [request, update],
  )

  const value = useMemo(
    () => ({
      chats,
      openChat,
      closeChat,
      ask,
      stop,
      switchContext,
      keepTimeframe,
      retry,
    }),
    [
      chats,
      openChat,
      closeChat,
      ask,
      stop,
      switchContext,
      keepTimeframe,
      retry,
    ],
  )

  return (
    <MinervaChatContext.Provider value={value}>
      {children}
    </MinervaChatContext.Provider>
  )
}

type UseMinervaChatResult = {
  /** False without a `MinervaChatProvider` and `MinervaChatLayout` above. */
  isAvailable: boolean
  chat: MinervaChatEntry | undefined
  panelSlot: HTMLElement | null
  openChat: (content: MinervaChatContent) => void
  closeChat: VoidFunction
  ask: (question: string) => void
  stop: VoidFunction
  switchContext: (content: MinervaChatSwitchContent) => void
  keepTimeframe: (timeframe: string) => void
  retry: (messageId: string) => void
}

export const useMinervaChat = (key: string): UseMinervaChatResult => {
  const context = useContext(MinervaChatContext)
  const layout = useContext(MinervaChatLayoutContext)
  const bridge = layout?.cubeBridge ?? null

  return {
    isAvailable: context !== null && layout !== null,
    chat: context?.chats[key],
    panelSlot: layout?.panelSlot ?? null,
    openChat: content => context?.openChat(key, content),
    closeChat: () => context?.closeChat(key),
    ask: question => context?.ask(key, question, bridge),
    stop: () => context?.stop(key),
    switchContext: content => context?.switchContext(key, content),
    keepTimeframe: timeframe => context?.keepTimeframe(key, timeframe),
    retry: messageId => context?.retry(key, messageId, bridge),
  }
}
